<template>
    <div class="space-y-6">
        <div>
            <h3 class="mb-3 text-content-lg font-semibold">Display Modes</h3>
            <div class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Fixed Mode (4 security chars)</h4>
                        <DsMaskedDisplay
                            displayMode="fixed"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :value="accountValue"
                        />
                    </div>

                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Dynamic Mode (actual length)</h4>
                        <DsMaskedDisplay
                            displayMode="dynamic"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :value="accountValue"
                        />
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Fixed Mode - Long Account</h4>
                        <DsMaskedDisplay
                            displayMode="fixed"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :value="longAccountValue"
                        />
                    </div>

                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Dynamic Mode - Long Account</h4>
                        <DsMaskedDisplay
                            displayMode="dynamic"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :value="longAccountValue"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div>
            <h3 class="mb-3 text-content-lg font-semibold">Separator Formatting Examples</h3>
            <div class="space-y-6">
                <div>
                    <h4 class="mb-3 text-sm font-semibold text-ds-primary-700">SSN (XXX-XX-XXXX)</h4>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <h5 class="mb-2 text-xs font-medium text-ds-primary-600">Fixed Mode + Toggleable</h5>
                            <DsMaskedDisplay
                                displayMode="fixed"
                                :isToggleable="true"
                                :maskType="MaskType.SSN"
                                :value="ssnValue"
                            />
                        </div>

                        <div>
                            <h5 class="mb-2 text-xs font-medium text-ds-primary-600">Dynamic Mode + Toggleable</h5>
                            <DsMaskedDisplay
                                displayMode="dynamic"
                                :isToggleable="true"
                                :maskType="MaskType.SSN"
                                :value="ssnValue"
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <h4 class="mb-3 text-sm font-semibold text-ds-primary-700">EIN (XX-XXXXXXX)</h4>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <h5 class="mb-2 text-xs font-medium text-ds-primary-600">Fixed Mode + Toggleable</h5>
                            <DsMaskedDisplay
                                displayMode="fixed"
                                :isToggleable="true"
                                :maskType="MaskType.EIN"
                                :value="einValue"
                            />
                        </div>

                        <div>
                            <h5 class="mb-2 text-xs font-medium text-ds-primary-600">Dynamic Mode + Toggleable</h5>
                            <DsMaskedDisplay
                                displayMode="dynamic"
                                :isToggleable="true"
                                :maskType="MaskType.EIN"
                                :value="einValue"
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <h4 class="mb-3 text-sm font-semibold text-ds-primary-700">Account Number (No Separators)</h4>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <h5 class="mb-2 text-xs font-medium text-ds-primary-600">Fixed Mode + Toggleable</h5>
                            <DsMaskedDisplay
                                displayMode="fixed"
                                :isToggleable="true"
                                :maskType="MaskType.ACCOUNT_NUMBER"
                                :value="accountValue"
                            />
                        </div>

                        <div>
                            <h5 class="mb-2 text-xs font-medium text-ds-primary-600">Dynamic Mode + Toggleable</h5>
                            <DsMaskedDisplay
                                displayMode="dynamic"
                                :isToggleable="true"
                                :maskType="MaskType.ACCOUNT_NUMBER"
                                :value="accountValue"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <h3 class="mb-3 text-content-lg font-semibold">Toggleable Variants</h3>
            <div class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Fixed Mode + Toggleable</h4>
                        <DsMaskedDisplay
                            displayMode="fixed"
                            :isToggleable="true"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :value="accountValue"
                        />
                    </div>

                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Dynamic Mode + Toggleable</h4>
                        <DsMaskedDisplay
                            displayMode="dynamic"
                            :isToggleable="true"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :value="accountValue"
                        />
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">SSN - Fixed + Toggleable</h4>
                        <DsMaskedDisplay
                            displayMode="fixed"
                            :isToggleable="true"
                            :maskType="MaskType.SSN"
                            :value="ssnValue"
                        />
                    </div>

                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">EIN - Dynamic + Toggleable</h4>
                        <DsMaskedDisplay
                            displayMode="dynamic"
                            :isToggleable="true"
                            :maskType="MaskType.EIN"
                            :value="einValue"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div>
            <h3 class="mb-3 text-content-lg font-semibold">Custom Visible Characters</h3>
            <div class="space-y-4">
                <div class="grid grid-cols-3 gap-4">
                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Last 2 digits - Fixed</h4>
                        <DsMaskedDisplay
                            displayMode="fixed"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :numVisibleCharacters="2"
                            :value="accountValue"
                        />
                    </div>

                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Last 4 digits - Dynamic</h4>
                        <DsMaskedDisplay
                            displayMode="dynamic"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :numVisibleCharacters="4"
                            :value="accountValue"
                        />
                    </div>

                    <div>
                        <h4 class="mb-2 text-sm font-medium text-ds-primary-600">Last 6 digits - Fixed + Toggleable</h4>
                        <DsMaskedDisplay
                            displayMode="fixed"
                            :isToggleable="true"
                            :maskType="MaskType.ACCOUNT_NUMBER"
                            :numVisibleCharacters="6"
                            :value="accountValue"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsMaskedDisplay from '@/components/design-system/DsMaskedDisplay.vue';
import { MaskType } from '@ds/form/input/text/text-input.types';
import { ref } from 'vue';

const ssnValue = ref('123456789');
const einValue = ref('123456789');
const accountValue = ref('1234567890');
const longAccountValue = ref('1234567890123456');
</script>
